import { Image, Link, Text, View } from '@react-pdf/renderer'
import React from 'react'
import { tw } from '~/components/pdfs'
import type { Company, ListingBroker, User } from '~/models'
import {
  cn,
  fmtAddress,
  fmtName,
  fmtPhoneNumber,
  getPhoneHref,
  transformImageUrl,
} from '~/utils'

export default function PdfBrokerProfile({
  orientation,
  user,
  company,
  brokers,
}: {
  orientation: 'portrait' | 'landscape'
  user: User
  company?: Company
  brokers?: ListingBroker[]
}) {
  // if (!pdfOptions.showBrokerProfile) return null

  // No use callback as we're running on server too
  const renderBrokers = () => {
    if ((brokers ?? []).length === 0) {
      return (
        <>
          <View style={tw('flex flex-row justify-center')}>
            {user.meta?.photo ? (
              <Image
                style={tw('w-36 rounded-full')}
                src={transformImageUrl(user.meta?.photo?.url)}
              />
            ) : (
              <View
                style={tw(
                  'flex h-36 w-36 flex-row items-center justify-center rounded-full bg-white'
                )}>
                <Text style={tw('mt-2 text-2xl font-medium')}>
                  {[user.firstName.slice(0, 1), user.lastName.slice(0, 1)]
                    .join('')
                    .toUpperCase()}
                </Text>
              </View>
            )}
          </View>
          <View style={tw('mt-8 flex flex-col gap-1')}>
            <View style={tw('flex flex-row')}>
              <Text style={tw('text-base font-bold')}>
                {fmtName(user.firstName, user.middleName, user.lastName)}
              </Text>
              {user.meta?.memberships && (
                <Text style={tw('ml-2 text-base text-muted-foreground')}>
                  {user.meta.memberships.split(/\s+|,\s+|\|/).join(', ')}
                </Text>
              )}
            </View>
            {user.meta?.title && (
              <Text style={tw('text-sm')}>{user.meta.title}</Text>
            )}
            {user.email && (
              <View style={tw('mt-1 flex flex-row')}>
                <Text style={tw('w-4 text-sm')}>E:</Text>
                <Link
                  style={tw('text-sm text-primary no-underline')}
                  href={`mailto:${user.email}`}>
                  <Text>{user.email}</Text>
                </Link>
              </View>
            )}
            {user.meta?.office && (
              <View style={tw('mt-1 flex flex-row')}>
                <Text style={tw('w-4 text-sm')}>O:</Text>
                <Link
                  style={tw('text-sm text-primary no-underline')}
                  href={getPhoneHref(
                    user.meta?.office,
                    user.meta?.officeExtension
                  )}>
                  {fmtPhoneNumber(user.meta.office, user.meta.officeExtension)}
                </Link>
              </View>
            )}
            {user.meta?.mobile && (
              <View style={tw('mt-1 flex flex-row')}>
                <Text style={tw('w-4 text-sm')}>M:</Text>
                <Link
                  style={tw('text-sm text-primary no-underline')}
                  href={`tel:${user.meta?.mobile}`}>
                  {fmtPhoneNumber(user.meta.mobile)}
                </Link>
              </View>
            )}
          </View>
        </>
      )
    }
    return (
      <>
        {[user, ...brokers!].map((broker) => {
          return (
            <React.Fragment key={broker._id}>
              <View style={tw('mt-6 flex flex-row items-center gap-2')}>
                {broker.meta?.photo && (
                  <View style={tw('h-10 w-10 shrink-0')}>
                    <Image
                      style={tw('rounded-full')}
                      src={transformImageUrl(broker.meta?.photo?.url)}
                    />
                  </View>
                )}
                <View>
                  <View
                    style={tw(
                      'flex flex-row justify-start gap-4 overflow-hidden'
                    )}>
                    <Text style={tw('text-base font-bold')}>
                      {fmtName(
                        broker.firstName,
                        broker.middleName,
                        broker.lastName
                      )}
                    </Text>
                    {broker.meta?.memberships && (
                      <Text style={tw('text-base text-muted-foreground')}>
                        {broker.meta.memberships
                          .split(/\s+|,\s+|\|/)
                          .join(', ')}
                      </Text>
                    )}
                  </View>
                  {broker.meta?.title && (
                    <Text style={tw('text-sm')}>{broker.meta.title}</Text>
                  )}
                </View>
              </View>
              <View style={tw('flex-col gap-1')}>
                {broker.email && (
                  <View style={tw('mt-1 flex flex-row')}>
                    <Text style={tw('w-4 text-sm')}>E:</Text>
                    <Link
                      style={tw('text-sm text-primary no-underline')}
                      href={`mailto:${broker.email}`}>
                      <Text>{broker.email}</Text>
                    </Link>
                  </View>
                )}
                {broker.meta?.office && (
                  <View style={tw('mt-1 flex flex-row')}>
                    <Text style={tw('w-4 text-sm')}>O:</Text>
                    <Link
                      style={tw('text-sm text-primary no-underline')}
                      href={getPhoneHref(
                        broker.meta?.office,
                        broker.meta?.officeExtension
                      )}>
                      {fmtPhoneNumber(
                        broker.meta.office,
                        broker.meta.officeExtension
                      )}
                    </Link>
                  </View>
                )}
                {broker.meta?.mobile && (
                  <View style={tw('mt-1 flex flex-row')}>
                    <Text style={tw('w-4 text-sm')}>M:</Text>
                    <Link
                      style={tw('text-sm text-primary no-underline')}
                      href={`tel:${broker.meta?.mobile}`}>
                      {fmtPhoneNumber(broker.meta.mobile)}
                    </Link>
                  </View>
                )}
              </View>
            </React.Fragment>
          )
        })}
      </>
    )
  }

  return (
    <View
      style={tw(
        cn(
          'w-1/3 bg-gray-100 px-6 py-8',
          orientation === 'portrait' ? 'h-[792px]' : 'h-[612px]'
        )
      )}>
      {renderBrokers()}

      {company && (
        <View style={tw('mt-8 border-t border-gray-300 pt-8')}>
          <View style={tw('flex flex-row justify-center gap-4')}>
            {company.meta.logo && (
              <Image
                style={tw('max-h-36')}
                src={transformImageUrl(company.meta.logo.url)}
              />
            )}
          </View>
          <Text style={tw('mt-4 font-sans text-base font-bold')}>
            {company.name}
          </Text>
          {company.meta.address && (
            <Text style={tw('text-base')}>{fmtAddress(company.meta)}</Text>
          )}
        </View>
      )}
    </View>
  )
}
